// src/pages/AuditLogs/components/AuditLogsTable.tsx
// Table component to display audit logs list

import React from 'react';
import { Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AuditLog } from '../types';
import { clearFilters } from 'slices/auditLogs/reducer';
import DataTableShell, { getSttNumber } from 'Components/Common/DataTableShell';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('audit_logs');

interface AuditLogsTableProps {
  data: AuditLog[];
  loading: boolean;
  page: number;
  pageSize: number;
}

const AuditLogsTable: React.FC<AuditLogsTableProps> = ({ data, loading, page, pageSize }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Get badge color and icon based on result
  const getResultBadge = (result: string) => {
    const config = {
      Success: { color: 'success', icon: 'ri-checkbox-circle-line' },
      Failed: { color: 'danger', icon: 'ri-close-circle-line' },
      Warning: { color: 'warning', icon: 'ri-error-warning-line' }
    };
    
    const { color, icon } = config[result as keyof typeof config] || config.Success;
    
    return (
      <Badge color={color} className="d-inline-flex align-items-center">
        <i className={`${icon} me-1`}></i>
        {result}
      </Badge>
    );
  };
  
  // Get badge color based on action type
  const getActionBadge = (action: string) => {
    const colorMap: Record<string, string> = {
      Login: 'primary',
      Logout: 'secondary',
      Create: 'success',
      Update: 'warning',
      Delete: 'danger',
      View: 'info'
    };
    
    return (
      <Badge color={colorMap[action] || 'secondary'}>
        {action}
      </Badge>
    );
  };
  
  // Format timestamp to readable format
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Handle clear filters
  const handleClearFilters = () => {
    dispatch(clearFilters());
  };
  
  // Loading state with skeleton
  if (loading) {
    return (
      <DataTableShell
        headerRow={
          <tr>
            <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
            <th>{st('thoi_gian_column')}</th>
            <th>{st('nguoi_dung_column')}</th>
            <th>{st('hanh_dong_label')}</th>
            <th>{st('module_label')}</th>
            <th>{st('mo_ta_column')}</th>
            <th>{st('ket_qua_label')}</th>
            {/* <th>{t('auditlogs_field_duration')}</th> */}
            {/* <th>Actions</th> */}
          </tr>
        }
      >
        {[...Array(5)].map((_, idx) => (
          <tr key={idx}>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-4"></span>
              </div>
            </td>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-10"></span>
              </div>
            </td>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-8"></span>
              </div>
            </td>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </td>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-7"></span>
              </div>
            </td>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-12"></span>
              </div>
            </td>
            <td>
              <div className="placeholder-glow">
                <span className="placeholder col-6"></span>
              </div>
            </td>
          </tr>
        ))}
      </DataTableShell>
    );
  }
  
  // Empty state
  if (data.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="mb-4">
          <i className="ri-file-list-line display-3 text-muted"></i>
        </div>
        {/* <h5 className="mt-3">{t('auditlogs_no_data')}</h5> */}
        <p className="text-muted mb-4">{st('khong_co_du_lieu_de_hien_text')}</p>
        <button 
          className="btn btn-primary"
          onClick={handleClearFilters}
        >
          <i className="ri-close-circle-line me-1"></i>
          {st('xoa_bo_loc_button')}
        </button>
      </div>
    );
  }
  
  return (
    <DataTableShell
      headerRow={
        <tr>
          <th className="text-center" style={{ width: 60 }}>{st('stt_column')}</th>
          <th>{st('thoi_gian_column')}</th>
          <th>{st('nguoi_dung_column')}</th>
          <th>{st('hanh_dong_label')}</th>
          <th>{st('module_label')}</th>
          <th>{st('mo_ta_column')}</th>
          <th>{st('ket_qua_label')}</th>
          {/* <th>{t('auditlogs_field_duration')}</th>
          <th>Actions</th> */}
        </tr>
      }
    >
          {data.map((log, rowIndex) => (
            <tr key={log.id}>
              <td className="text-center">{getSttNumber(page, pageSize, rowIndex)}</td>
              <td className="text-nowrap">
                <small>{formatTimestamp(log.timestamp)}</small>
              </td>
              <td>
                <div className="d-flex flex-column">
                  <span className="fw-medium">{log.username}</span>
                  <small className="text-muted">{log.email}</small>
                  {log.ipAddress && (
                    <small className="text-muted">
                      <i className="ri-computer-line me-1"></i>
                      {log.ipAddress}
                    </small>
                  )}
                </div>
              </td>
              <td>{getActionBadge(log.action)}</td>
              <td>
                <Badge color="light" className="text-dark">
                  {log.module}
                </Badge>
              </td>
              <td>
                <span 
                  className="text-truncate d-inline-block" 
                  style={{ maxWidth: '300px' }}
                  title={log.description}
                >
                  {log.description}
                </span>
                {log.entityType && (
                  <div>
                    <small className="text-muted">
                      {log.entityType} #{log.entityId}
                    </small>
                  </div>
                )}
              </td>
              <td>{getResultBadge(log.result)}</td>
              {/* <td>
                {log.duration ? (
                  <span className="text-muted">
                    {log.duration}ms
                  </span>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
              <td>
                <button 
                  className="btn btn-sm btn-soft-info"
                  title="View Details"
                >
                  <i className="ri-eye-line"></i>
                </button>
              </td> */}
            </tr>
          ))}
    </DataTableShell>
  );
};

export default AuditLogsTable;